import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Input, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { AsyncDispatch } from '../../../types/global';
import { ApplicationState } from '../../reducers/index';
import { fetchLoadSheets, fetchAccountEBPTemplates } from '../../actions/load_sheets/fetch';
import { fetchFullReceiverList } from '../../actions/receiver/fetch';
import { deleteLoadSheet } from '../../actions/load_sheets/delete';
import { resetExportBuilderAdvanced } from '../../actions/brand/export_builder_advanced/update';
import LoadSheetsTable from '../../components/body/load_sheets/LoadSheetsTable';
import LoadSheetUploadForm from '../../components/body/load_sheets/LoadSheetUploadForm';
import LoadSheetFilePreviewDrawer from '../../components/body/load_sheets/LoadSheetFilePreviewDrawer';
import { typingDone } from '../../utils/Utils';
import { isReceiver } from '../../utils/UserUtils';
import { useAppDispatch } from '../../store/ReduxStore';

const LoadSheetsContainer: React.FC = () => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();

  const loadSheets = useSelector((state: ApplicationState) => state.loadSheets.loadSheets);
  const isLoading = useSelector((state: ApplicationState) => state.loadSheets.isLoading);
  const user = useSelector((state: ApplicationState) => state.user.user);

  const receiver = React.useMemo(() => isReceiver(user!), [user]);

  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [showEditPage, setShowEditPage] = useState(false);
  const [showPreviewDrawer, setShowPreviewDrawer] = useState(false);
  const [selectedPreviewTemplate, setSelectedPreviewTemplate] = useState<string | null>(null);
  const [selectedPreviewTemplateName, setSelectedPreviewTemplateName] = useState<string | null>(
    null
  );
  const [isPreviewById, setIsPreviewById] = useState(false);
  const [filterKeywords, setFilterKeywords] = useState('');

  useEffect(() => {
    dispatch(fetchLoadSheets());
    dispatch(fetchFullReceiverList());
    if (receiver) dispatch(fetchAccountEBPTemplates());
  }, [dispatch, receiver]);

  const handleEdit = (id: number) => {
    setSelectedTemplateId(id);
    setShowEditPage(true);
  };

  const handleConfirmDelete = async (selectedTemplateId: number) => {
    await dispatch(deleteLoadSheet(selectedTemplateId));
    dispatch(fetchLoadSheets());
    setSelectedTemplateId(null);
  };

  const handleDelete = (selectedTemplateId: number) => {
    Modal.confirm({
      title: t('loadSheets:deleteConfirmTitle'),
      icon: <ExclamationCircleOutlined />,
      okText: t('common:delete'),
      cancelText: t('common:cancel'),
      okButtonProps: { danger: true },
      onOk() {
        return handleConfirmDelete(selectedTemplateId);
      },
    });
  };

  const handlePreviewById = (id: number, templateName: string) => {
    setSelectedPreviewTemplate(id.toString());
    setSelectedPreviewTemplateName(templateName);
    setIsPreviewById(true);
    setShowPreviewDrawer(true);
    setSelectedTemplateId(id);
  };

  const showCreateDrawer = () => {
    setShowEditPage(true);
  };

  const fetchNextLoadSheets = (page: number) => {
    return dispatch(fetchLoadSheets(page));
  };

  const closeDrawer = () => {
    setSelectedTemplateId(null);
    setShowEditPage(false);
  };

  return (
    <div className="user__wrapper">
      <div className="page-layout__top-bar">
        <div className="page-layout__top-bar__container">
          <Input.Search
            className="user__search-input"
            value={filterKeywords}
            placeholder={t('loadSheets:searchLoadsheets')}
            onChange={e => {
              setFilterKeywords(e.target.value);
              typingDone(() => dispatch(fetchLoadSheets(1, e.target.value)));
            }}
            allowClear
          />
          {receiver && (
            <div className="page-layout__top-bar__actions">
              <Button type="primary" onClick={() => showCreateDrawer()}>
                {t('loadSheets:addLoadSheet')}
              </Button>
            </div>
          )}
        </div>
      </div>

      <LoadSheetsTable
        loadSheets={loadSheets}
        fetchingLoadSheets={isLoading}
        receiver={receiver}
        fetchNextLoadSheets={fetchNextLoadSheets}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onPreview={handlePreviewById}
      />

      <LoadSheetFilePreviewDrawer
        visible={showPreviewDrawer}
        receiver={receiver}
        templateName={selectedPreviewTemplate}
        templateFileName={selectedPreviewTemplateName}
        isPreviewById={isPreviewById}
        selectedTemplateId={selectedTemplateId}
        selectedLoadSheet={loadSheets?.find(loadSheet => loadSheet.id === selectedTemplateId)}
        onClose={() => {
          setShowPreviewDrawer(false);
          setSelectedPreviewTemplate(null);
          setSelectedPreviewTemplateName(null);
          setIsPreviewById(false);
          resetExportBuilderAdvanced();
        }}
      />

      {showEditPage && (
        <LoadSheetUploadForm
          visible={showEditPage}
          currentTemplateId={selectedTemplateId}
          selectedLoadSheet={loadSheets?.find(loadSheet => loadSheet.id === selectedTemplateId)}
          receiver={receiver}
          onClose={() => closeDrawer()}
        />
      )}
    </div>
  );
};

export default LoadSheetsContainer;
